/**
 * MealManagement - CRUD for event meals/dining options
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Utensils, Clock, Loader2 } from 'lucide-react';
import { mealAPI } from '@/lib/api';
import { TimePicker } from './TimePicker';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'refreshments', 'cocktail'];
const TYPE_COLORS = {
  breakfast: 'bg-yellow-100 text-yellow-800',
  lunch: 'bg-green-100 text-green-800',
  dinner: 'bg-purple-100 text-purple-800',
  snack: 'bg-orange-100 text-orange-800',
  refreshments: 'bg-blue-100 text-blue-800',
  cocktail: 'bg-pink-100 text-pink-800'
};

// Form component
const MealForm = ({ meal, onSubmit, onCancel }) => {
  const [form, setForm] = useState(meal || {
    name: '',
    description: '',
    type: 'lunch',
    start_time: '',
    end_time: '',
    location: '',
    capacity: ''
  });

  const formatTime = (time) => time ? time.slice(0, 5) : '';

  return (
    <div className="space-y-4">
      <div>
        <Label>Name *</Label>
        <Input 
          value={form.name} 
          onChange={e => setForm({...form, name: e.target.value})} 
          placeholder="e.g., Welcome Lunch"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Type</Label>
          <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {MEAL_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Location</Label>
          <Input 
            value={form.location || ''} 
            onChange={e => setForm({...form, location: e.target.value})} 
            placeholder="Main Ballroom"
          />
        </div>
      </div>
      
      <div>
        <Label>Description</Label>
        <Textarea 
          value={form.description || ''} 
          onChange={e => setForm({...form, description: e.target.value})} 
          rows={2}
          placeholder="Menu details, dietary options available..."
        />
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <TimePicker 
          label="Start Time"
          value={form.start_time} 
          onChange={v => setForm({...form, start_time: v})}
        />
        <TimePicker 
          label="End Time"
          value={form.end_time} 
          onChange={v => setForm({...form, end_time: v})}
        />
        <div>
          <Label>Capacity</Label>
          <Input 
            type="number" 
            value={form.capacity || ''} 
            onChange={e => setForm({...form, capacity: parseInt(e.target.value) || ''})}
            placeholder="Optional"
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => {
          // Clean up form data - remove empty strings for optional fields
          const cleanData = { ...form };
          if (cleanData.capacity === '' || cleanData.capacity === null) {
            delete cleanData.capacity;
          }
          onSubmit(cleanData);
        }} disabled={!form.name}>
          {meal ? 'Update' : 'Create'} Meal
        </Button>
      </div>
    </div>
  );
};

const MealManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const { data: meals = [], isLoading } = useQuery({
    queryKey: ['meals', eventId],
    queryFn: () => mealAPI.adminList(eventId).then(r => {
      const data = r.data;
      if (Array.isArray(data)) return data;
      if (data?.data && Array.isArray(data.data)) return data.data;
      return [];
    }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => mealAPI.create(eventId, data),
    onSuccess: () => {
      toast.success('Meal created');
      queryClient.invalidateQueries(['meals', eventId]);
      setIsOpen(false);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create meal')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => mealAPI.update(eventId, id, data),
    onSuccess: () => {
      toast.success('Meal updated');
      queryClient.invalidateQueries(['meals', eventId]);
      setEditingMeal(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update meal')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => mealAPI.delete(eventId, id),
    onSuccess: () => {
      toast.success('Meal deleted');
      queryClient.invalidateQueries(['meals', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete meal')
  });

  const filteredMeals = filterType === 'all' 
    ? meals 
    : meals.filter(m => m.type === filterType);

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meal-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Utensils className="h-6 w-6" />
            Meals & Dining
          </h2>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {MEAL_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline">{filteredMeals.length} meals</Badge>
        </div>
        
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-meal-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Meal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Meal</DialogTitle>
            </DialogHeader>
            <MealForm 
              onSubmit={data => createMutation.mutate(data)} 
              onCancel={() => setIsOpen(false)} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {filteredMeals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Utensils className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500">No meals configured yet.</p>
            <p className="text-sm text-slate-400">Click "Add Meal" to create your first meal option.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredMeals.map(meal => (
            <Card key={meal.id} data-testid={`meal-${meal.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className={TYPE_COLORS[meal.type] || 'bg-gray-100 text-gray-800'}>
                      {meal.type}
                    </Badge>
                    <CardTitle className="mt-2">{meal.name}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditingMeal(meal)}
                      data-testid={`edit-meal-${meal.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => deleteMutation.mutate(meal.id)}
                      data-testid={`delete-meal-${meal.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {meal.description && (
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{meal.description}</p>
                )}
                <div className="space-y-1 text-sm text-slate-500">
                  {(meal.start_time || meal.end_time) && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{formatTime(meal.start_time)} - {formatTime(meal.end_time)}</span>
                    </div>
                  )}
                  {meal.location && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">📍</span>
                      <span>{meal.location}</span>
                    </div>
                  )}
                  {meal.capacity && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">👥</span>
                      <span>Capacity: {meal.capacity}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingMeal} onOpenChange={(open) => !open && setEditingMeal(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Meal</DialogTitle>
          </DialogHeader>
          {editingMeal && (
            <MealForm 
              meal={editingMeal}
              onSubmit={data => updateMutation.mutate({ id: editingMeal.id, data })} 
              onCancel={() => setEditingMeal(null)} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MealManagement;
